/**
 * FH Subcategory Stories
 * Custom Element for Instagram Stories-style subcategory slider
 * OOP pattern following FHBrandHighlights
 */

if (!customElements.get('fh-subcategory-stories')) {
  class FHSubcategoryStories extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.prevBtn = null;
      this.nextBtn = null;
      this.items = [];
      this.resizeObserver = null;
      this.scrollTimeout = null;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.prevBtn = this.querySelector('[data-slider-prev]');
      this.nextBtn = this.querySelector('[data-slider-next]');
      
      if (!this.track) return;
      
      this.items = Array.from(this.querySelectorAll('.fh-ss__item'));
      
      // Mark active item based on current URL
      this.highlightActiveItem();
      
      // Bind events
      this.bindEvents();
      
      // Initial navigation state (with small delay to ensure layout is complete)
      requestAnimationFrame(() => {
        this.updateNavigation();
      });

      // Re-check on resize
      this.resizeObserver = new ResizeObserver(() => {
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.updateNavigation();
        }, 100);
      });
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      clearTimeout(this.scrollTimeout);
    }

    /**
     * Highlight the item that matches the current URL
     * Handles both regular collection URLs and tag-filtered URLs
     */
    highlightActiveItem() {
      const currentPath = window.location.pathname;
      
      this.items.forEach(item => {
        const itemUrl = item.getAttribute('href');
        if (!itemUrl) return;
        
        // Check if current path ends with the item URL or matches exactly
        // This handles both /de-de/collections/xyz and /collections/xyz patterns
        const normalizedItemUrl = itemUrl.replace(/^\/+/, '/');
        const normalizedCurrentPath = currentPath.replace(/^\/+/, '/');
        
        // Match if paths end the same way (handles market prefixes)
        const isActive = normalizedCurrentPath.endsWith(normalizedItemUrl) || 
                         normalizedCurrentPath === normalizedItemUrl ||
                         normalizedCurrentPath.includes(normalizedItemUrl);
        
        item.classList.toggle('fh-ss__item--active', isActive);
      });
    }

    bindEvents() {
      // Navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollPrev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollNext());
      }

      // Update navigation state on scroll (debounced)
      let scrollTimer;
      this.track.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          this.updateNavigation();
        }, 50);
      });

      // Touch events for better mobile experience
      let touchStartX = 0;
      this.track.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
      }, { passive: true });
    }

    /**
     * Calculate scroll amount based on item width and gap
     */
    getScrollAmount() {
      const item = this.track.querySelector('.fh-ss__item');
      if (!item) return 100;
      
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.gap) || 0;
      const itemWidth = item.offsetWidth + gap;
      
      // On mobile/tablet, scroll 3 items; on desktop, scroll 4
      const itemsToScroll = window.innerWidth < 750 ? 3 : 4;
      return itemWidth * itemsToScroll;
    }

    scrollPrev() {
      const scrollAmount = this.getScrollAmount();
      this.track.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }

    scrollNext() {
      const scrollAmount = this.getScrollAmount();
      this.track.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }

    /**
     * Update navigation button states and visibility
     */
    updateNavigation() {
      const navContainer = this.querySelector('.fh-ss__nav');
      
      if (!this.track) return;
      
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const canScroll = scrollWidth > clientWidth + 5; // 5px tolerance

      // Show/hide navigation based on whether scrolling is needed
      if (navContainer) {
        navContainer.classList.toggle('fh-ss__nav--hidden', !canScroll);
      }

      // Update disabled states for buttons
      if (this.prevBtn && this.nextBtn) {
        const isAtStart = scrollLeft <= 5;
        const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;

        this.prevBtn.disabled = isAtStart;
        this.nextBtn.disabled = isAtEnd;
      }
    }
  }

  customElements.define('fh-subcategory-stories', FHSubcategoryStories);
}
